import { useEffect, useState } from 'react'
import { Button, Input, Grid } from '@design-systems/canvas-ui'
import _ from 'lodash'
import Icon, {
  PlusCircleIcon,
  MinusCircleIcon,
} from '@design-systems/react-icons'
import {
  SectionWrapper,
  renderControlLabel,
  showErrorMessage,
} from '../Builders'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import { buildDropdownOptions, getControlError } from '../../common/helper'
import { VALUE_OTHER } from '../../common/commonConstants'

const LABEL_MATERIAL_GRP = 'Material Group'
const LABEL_MATERIAL_CAP = 'Material Capabilities'

const MaterialCapabilities = (props) => {
  const [sectionData, setSectionData] = useState(null)
  const [isTriggerSave, setIsTriggerSave] = useState(false)

  useEffect(() => {
    setSectionData(props.sectionData)
  }, [props.sectionData])

  useEffect(() => {
    if (isTriggerSave && !_.isEqual(props.sectionData, sectionData)) {
      props.saveRfiPayload(sectionData, props.sectionTemplate.uid)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateMCRowProperty = (property, rowIndex, newVal) => {
    let updatedMCGroupRows = null
    switch (property.toLowerCase()) {
      case 'material_group':
        updatedMCGroupRows = sectionData?.material_capability_list?.map(
          (eachRow, index) => {
            return rowIndex === index
              ? {
                  ...eachRow,
                  [property]: newVal ? newVal : null,
                  material_capabilities:
                    eachRow[property] === newVal
                      ? eachRow.material_capabilities
                      : [],
                }
              : eachRow
          },
        )
        break
      case 'other_material_group':
      case 'material_capabilities':
        updatedMCGroupRows = sectionData?.material_capability_list?.map(
          (eachRow, index) => {
            return rowIndex === index
              ? {
                  ...eachRow,
                  [property]: newVal ? newVal : null,
                }
              : eachRow
          },
        )
        break
      default:
        break
    }
    setSectionData({
      ...sectionData,
      material_capability_list: updatedMCGroupRows,
    })
  }

  const getUniqueMaterialGroups = () => {
    let materialGroupList = []
    props.rfiMasterData.rfi_location?.material_types?.forEach((eachGroup) => {
      materialGroupList.push(eachGroup.group)
    })
    return materialGroupList
  }

  const getCapabilitesForGroup = (groupName) => {
    let capabilities = []
    if (groupName) {
      capabilities = props.rfiMasterData.rfi_location?.material_types?.filter(
        (eachGroup) => eachGroup.group === groupName,
      )[0].capability
    }
    return capabilities
  }

  /**
   * Render the Material Capabilities section body
   * @returns
   */
  const renderMaterialCapabilitiesView = () => {
    let mcPath = `${props.sectionPath}.material_capability_list`

    let designMaterialCapabilitiesRows =
      sectionData?.material_capability_list?.map((eachMCRow, eachMCIndex) => {
        let materialGroupError = getControlError(
          `${mcPath}[${eachMCIndex}].material_group`,
          props.errorCache,
        )
        let materialCapabilitiesError = getControlError(
          `${mcPath}[${eachMCIndex}].material_capabilities`,
          props.errorCache,
        )
        let otherMaterialGroupError = getControlError(
          `${mcPath}[${eachMCIndex}].other_material_group`,
          props.errorCache,
        )

        return (
          <Grid.Container className="hc8-pl-3x hc8-mb-2x">
            {/* Material Group */}
            <Grid.Item
              size={{ xs: 3 }}
              className="hc8-pl-none border-bottom-grey-thin"
            >
              <div style={{ width: '100%' }}>
                {renderControlLabel({
                  label_name: LABEL_MATERIAL_GRP,
                  is_mandatory: true,
                })}
                <AutocompleteInput
                  value={
                    eachMCRow.material_group ? eachMCRow.material_group : ''
                  }
                  error={materialGroupError}
                  class="font-size-16 height-32p"
                  options={buildDropdownOptions(
                    getUniqueMaterialGroups(),
                    null,
                    true,
                    false,
                  )}
                  onUpdate={(value) => {
                    updateMCRowProperty(
                      'material_group',
                      eachMCIndex,
                      value?.value,
                    )
                  }}
                />
                {materialGroupError && (
                  <div className="hc8-pt-2x hc8-pl-1x">
                    {showErrorMessage(materialGroupError)}
                  </div>
                )}
              </div>
              {eachMCRow.material_group?.toLowerCase() === VALUE_OTHER && (
                <div className="hc8-pb-3x">
                  {/* Others */}
                  <Input.Text
                    key={`ac-other-material-${eachMCIndex}`}
                    className="hc8-pb-none"
                    placeholder={'Mention material group'}
                    value={
                      eachMCRow.other_material_group
                        ? eachMCRow.other_material_group
                        : ''
                    }
                    isErrored={!!otherMaterialGroupError}
                    onChange={(event) => {
                      updateMCRowProperty(
                        'other_material_group',
                        eachMCIndex,
                        event.target.value,
                      )
                    }}
                  />
                  {otherMaterialGroupError && (
                    <div className="hc8-pt-2x">
                      {showErrorMessage(otherMaterialGroupError)}
                    </div>
                  )}
                </div>
              )}
            </Grid.Item>
            <Grid.Item
              size={{ xs: 3 }}
              className="hc8-pl-3x border-bottom-grey-thin"
            >
              {/* Material Capabilities */}
              <div style={{ width: '100%' }}>
                {renderControlLabel({
                  label_name: LABEL_MATERIAL_CAP,
                  is_mandatory: true,
                })}
                <AutocompleteInput
                  value={buildDropdownOptions(
                    eachMCRow.material_capabilities
                      ? eachMCRow.material_capabilities
                      : '',
                    null,
                    false,
                    false,
                  )}
                  defaultPlaceholder={'Please select option(s)'}
                  disabled={!eachMCRow.material_group}
                  error={materialCapabilitiesError}
                  class="font-size-16"
                  options={buildDropdownOptions(
                    getCapabilitesForGroup(eachMCRow.material_group),
                    null,
                    true,
                    true,
                  )}
                  multiselect={true}
                  onUpdate={(value) => {
                    let newList = value.map((eachValue) => {
                      return eachValue.value
                    })
                    updateMCRowProperty(
                      'material_capabilities',
                      eachMCIndex,
                      newList,
                    )
                  }}
                />
                <div className="hc8-pt-2x hc8-pl-1x">
                  {showErrorMessage(materialCapabilitiesError)}
                </div>
              </div>
            </Grid.Item>
            <Grid.Item size={{ xs: 1 }} className="align-centre-left">
              <Button
                key={`delete-ds-row-${eachMCIndex}`}
                id={`delete-ds-row-${eachMCIndex}`}
                data-testid={`delete-ds-row-${eachMCIndex}`}
                onClick={() => {
                  let data = [...sectionData.material_capability_list]
                  data.splice(eachMCIndex, 1)
                  setSectionData({
                    ...sectionData,
                    material_capability_list: data,
                  })
                }}
                variant="ghost"
                size="sm"
                isIconOnly
                className="hc8-ml-6x"
                aria-label="Delete design service row"
              >
                <Icon svg={MinusCircleIcon} aria-hidden />
              </Button>
            </Grid.Item>
          </Grid.Container>
        )
      })

    return (
      <div>
        {designMaterialCapabilitiesRows}
        <Button
          key="btn-add-ds-row"
          id="btn-add-ds-row"
          data-testid="btn-add-ds-row"
          variant="ghost"
          className="hc8-pl-none hc8-mt-none"
          onClick={() => addNewMaterialCapabilitiesRow()}
        >
          <Icon svg={PlusCircleIcon} className="hc8-mr-2x" aria-hidden />
          Add More
        </Button>
      </div>
    )
  }

  /**
   * Add Material Capabilties row
   */
  const addNewMaterialCapabilitiesRow = () => {
    setSectionData({
      ...sectionData,
      material_capability_list: [
        ...sectionData.material_capability_list,
        {
          material_group: '',
          material_capabilities: [],
        },
      ],
    })
  }

  return (
    <SectionWrapper
      sectionTemplate={props.sectionTemplate}
      setIsTriggerSave={setIsTriggerSave}
      sectionBody={renderMaterialCapabilitiesView()}
      sectionPath={props.sectionPath}
      errorCache={props.errorCache}
    />
  )
}

export default MaterialCapabilities
